import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';

// Contexts
import { AuthProvider } from './contexts/AuthContext'; 
import { CartProvider } from './contexts/CartContext';
import { CourseProvider } from './contexts/CourseContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Components
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/common/Layout';
import AdminLayout from './components/common/AdminLayout';
import ToastContainer from './components/common/ToastContainer';

import ErrorBoundary from './components/common/ErrorBoundary';

/* ------------------------------------------------------------------
   Route-level code splitting.

   Every page below used to be a static import, so a visitor landing on
   the homepage downloaded the admin dashboard, the instructor analytics
   charts and every internship screen before anything rendered. Each page
   is now its own chunk, fetched when its route is first visited.

   Home stays eager: it is the landing route, and lazy-loading it would
   trade a smaller bundle for a spinner on the page that matters most.
   ------------------------------------------------------------------ */
import Home from './pages/Home';

// Public
const Courses = lazy(() => import('./pages/Courses'));
const CourseLearning = lazy(() => import('./pages/CourseLearning'));
const CourseDetailResponsive = lazy(() => import('./pages/CourseDetailResponsive'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Resources = lazy(() => import('./pages/Resources'));
const PyqHome = lazy(() => import('./pages/pyq/PyqHome'));
const PyqSemester = lazy(() => import('./pages/pyq/PyqSemester'));
const PyqSubject = lazy(() => import('./pages/pyq/PyqSubject'));
const PyqList = lazy(() => import('./pages/pyq/PyqList'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderSuccess = lazy(() => import('./pages/OrderSuccess'));

// Auth
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'));
const AuthSuccess = lazy(() => import('./pages/auth/AuthSuccess'));

// Utility
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Blog = lazy(() => import('./pages/blog/Blog'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Contact = lazy(() => import('./pages/Contact'));
const Help = lazy(() => import('./pages/Help'));
const PrivacyPolicy = lazy(() => import('./pages/Privacy'));
const TermsAndConditions = lazy(() => import('./pages/Terms'));

// Student
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const StudentOrders = lazy(() => import('./pages/student/StudentOrders'));
const Notifications = lazy(() => import('./pages/student/Notifications'));
const Certificates = lazy(() => import('./pages/student/CertificatesAdvanced'));
const Profile = lazy(() => import('./pages/student/Profile'));
const ProgressAnalytics = lazy(() => import('./pages/student/ProgressAnalytics'));
const Wishlist = lazy(() => import('./pages/student/Wishlist'));
const LearningPage = lazy(() => import('./pages/LearningPage'));

// Instructor
const InstructorDashboard = lazy(() => import('./pages/Instructor/InstructorDashboard'));
const InstructorCourses = lazy(() => import('./pages/Instructor/InstructorCourses'));
const InstructorCreateCourse = lazy(() => import('./pages/Instructor/InstructorCreateCourse'));
const InstructorStudents = lazy(() => import('./pages/Instructor/InstructorStudents'));
const InstructorEarnings = lazy(() => import('./pages/Instructor/InstructorEarnings'));
const InstructorAnalyticsResponsive = lazy(() => import('./pages/Instructor/InstructorAnalyticsResponsive'));

// Admin
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminProjects = lazy(() => import('./pages/admin/AdminProjects'));
const AdminCourses = lazy(() => import('./pages/admin/AdminCourses'));
const AdminCourseLearning = lazy(() => import('./pages/admin/AdminCourseLearning'));
const AdminLectures = lazy(() => import('./pages/admin/AdminLectures'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminFaq = lazy(() => import('./pages/admin/AdminFaq'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminInstructors = lazy(() => import('./pages/admin/AdminInstructors'));
const AdminPYQ = lazy(() => import('./pages/admin/AdminPYQ'));
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'));
const AdminContact = lazy(() => import('./pages/admin/AdminContact'));
const AdminNewsletter = lazy(() => import('./pages/admin/AdminNewsletter'));
const AdminApprovals = lazy(() => import('./pages/admin/AdminApprovals'));
const AdminInterviews = lazy(() => import('./pages/admin/AdminInterviews'));
const AdminTaskReviews = lazy(() => import('./pages/admin/AdminTaskReviews'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminInternshipOpenings = lazy(() => import('./pages/admin/AdminInternshipOpenings'));

// Internship
const InternshipHome = lazy(() => import('./pages/internship/InternshipHome'));
const InternshipOpening = lazy(() => import('./pages/internship/InternshipOpening'));
const InternshipApply = lazy(() => import('./pages/internship/InternshipApply'));
const InternshipStatus = lazy(() => import('./pages/internship/InternshipStatus'));
const InternshipApplicationSuccess = lazy(() => import('./pages/internship/InternshipApplicationSuccess'));
const InterviewScheduler = lazy(() => import('./pages/internship/InterviewScheduler'));
const InterviewConfirmation = lazy(() => import('./pages/internship/InterviewConfirmation'));
const InternshipDashboardStudent = lazy(() => import('./pages/internship/InternshipDashboardStudent'));
const InternshipAdmin = lazy(() => import('./pages/internship/InternshipAdmin'));
const AdminChat = lazy(() => import('./pages/internship/AdminChat'));
const InternshipTasks = lazy(() => import('./pages/internship/InternshipTasks'));
const InternshipOffer = lazy(() => import('./pages/internship/InternshipOffer'));
const InternshipPayments = lazy(() => import('./pages/internship/InternshipPayments'));
const InternshipTimeline = lazy(() => import('./pages/internship/InternshipTimeline'));
const CertificateVerify = lazy(() => import('./pages/certificates/CertificateVerify'));

/** Shown while a route chunk is in flight. */
function RouteFallback() {
  return (
    <div style={styles.routeFallback} role="status" aria-live="polite">
      <div style={styles.routeSpinner} aria-hidden="true" />
      <span style={styles.routeFallbackText}>Loading…</span>
    </div>
  );
}

function RootContent() {
  const location = useLocation();

  const pathname = location.pathname || '/';
  const isAdminPath = pathname.startsWith('/admin');

  return (
    <div className="App">
      <ToastContainer />
      {/* Public website + non-admin routes with Header/Footer */}
      {!isAdminPath && (
        <Layout>
          <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/courses" element={<Courses />} />
          {/* New slug-based learning routes (isolated from legacy /course/:id) */}
          <Route path="/courses-learning" element={<CourseLearning />} />
          {/* Both point at CourseLearning: it reads :lessonId and resumes
              there, keeping the player, curriculum rail and progress in one
              view. Routing the lesson URL to a separate CourseLesson page
              swapped the whole layout out mid-course. */}
          <Route path="/courses/:slug" element={<CourseLearning />} />
          <Route path="/courses/:slug/lesson/:lessonId" element={<CourseLearning />} />
          <Route path="/course/:id" element={<CourseDetailResponsive />} />
          <Route path="/internship" element={<InternshipHome />} /> 
          <Route path="/internship/opening/:id" element={<InternshipOpening />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/resources" element={<Resources />} /> 
          {/* Legacy route now points to advanced PYQ flow */}
          <Route path="/pyq-papers" element={<PyqHome />} />
          {/* PYQ papers. Served under both /pyq (the inclusive path, used in
              links and canonicals) and /aktu/pyq, which is kept because it is
              already indexed and AKTU remains a supported syllabus. */}
          <Route path="/pyq" element={<PyqHome />} />
          <Route path="/pyq/:degreeSlug" element={<PyqSemester />} />
          {/* Support both legacy  and new  style URLs */}
          <Route path="/pyq/:degreeSlug/sem-:semester" element={<PyqSubject />} />
          <Route path="/pyq/:degreeSlug/sem-:semester/:subjectSlug" element={<PyqList />} />
          <Route path="/pyq/:degreeSlug/sem/:semester" element={<PyqSubject />} />
          <Route path="/pyq/:degreeSlug/sem/:semester/:subjectSlug" element={<PyqList />} />
          <Route path="/aktu/pyq" element={<PyqHome />} />
          <Route path="/aktu/pyq/:degreeSlug" element={<PyqSemester />} />
          <Route path="/aktu/pyq/:degreeSlug/sem-:semester" element={<PyqSubject />} />
          <Route path="/aktu/pyq/:degreeSlug/sem-:semester/:subjectSlug" element={<PyqList />} />
          <Route path="/aktu/pyq/:degreeSlug/sem/:semester" element={<PyqSubject />} />
          <Route path="/aktu/pyq/:degreeSlug/sem/:semester/:subjectSlug" element={<PyqList />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-success" element={<OrderSuccess />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/help" element={<Help />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} /> 
          <Route path="/faq" element={<Help />} /> 
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          {/* Support both /forgot and /forgot-password URLs for reset flow */}
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} /> 
          <Route path="/reset-password/:token" element={<ResetPassword />} /> 
          <Route path="/auth-success" element={<AuthSuccess />} />
          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['student', 'instructor', 'admin']} element={<Dashboard />} />} />
          <Route path="/student-dashboard" element={<ProtectedRoute allowedRoles={['student']} element={<StudentDashboard />} />} />
          <Route path="/instructor-dashboard" element={<ProtectedRoute allowedRoles={['instructor']} element={<InstructorDashboard />} />} />
          <Route path="/student/orders" element={<ProtectedRoute allowedRoles={['student']} element={<StudentOrders />} />} />
          <Route path="/student/profile" element={<ProtectedRoute allowedRoles={['student', 'instructor', 'admin']} element={<Profile />} />} />
          <Route path="/student/progress" element={<ProtectedRoute allowedRoles={['student']} element={<ProgressAnalytics />} />} />
          <Route path="/student/certificates" element={<ProtectedRoute allowedRoles={['student']} element={<Certificates />} />} />
          <Route path="/student/wishlist" element={<ProtectedRoute allowedRoles={['student']} element={<Wishlist />} />} />
          <Route path="/student/notifications" element={<ProtectedRoute allowedRoles={['student']} element={<Notifications />} />} />
          <Route path="/learning/:courseId" element={<ProtectedRoute allowedRoles={['student']} element={<LearningPage />} />} />
          <Route path="/internship/apply/:id" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipApply />} />} />
          <Route path="/internship/status" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipStatus />} />} />
          <Route path="/internship/application-success" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipApplicationSuccess />} />} />
          <Route path="/internship/scheduler" element={<ProtectedRoute allowedRoles={['student']} element={<InterviewScheduler />} />} />
          <Route path="/internship/confirmation" element={<ProtectedRoute allowedRoles={['student']} element={<InterviewConfirmation />} />} />
          <Route path="/internship/dashboard" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipDashboardStudent />} />} />
          <Route path="/internship/chat" element={<ProtectedRoute allowedRoles={['student', 'admin']} element={<AdminChat />} />} />
          <Route path="/internship/tasks" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipTasks />} />} />
          <Route path="/internship/offer/:id" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipOffer />} />} />
          <Route path="/internship/payments" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipPayments />} />} />
          <Route path="/internship/timeline" element={<ProtectedRoute allowedRoles={['student']} element={<InternshipTimeline />} />} />
          <Route path="/certificate/verify" element={<CertificateVerify />} />
          <Route path="/certificate/verify/:code" element={<CertificateVerify />} />
          <Route path="/instructor/courses" element={<ProtectedRoute allowedRoles={['instructor', 'admin']} element={<InstructorCourses />} />} />
          <Route path="/instructor/create-course" element={<ProtectedRoute allowedRoles={['instructor', 'admin']} element={<InstructorCreateCourse />} />} />
          <Route path="/instructor/students" element={<ProtectedRoute allowedRoles={['instructor', 'admin']} element={<InstructorStudents />} />} />
          <Route path="/instructor/earnings" element={<ProtectedRoute allowedRoles={['instructor', 'admin']} element={<InstructorEarnings />} />} />
          <Route path="/instructor/analytics" element={<ProtectedRoute allowedRoles={['instructor', 'admin']} element={<InstructorAnalyticsResponsive />} />} />

          {/* Root route always shows public Home page so that "Home"
              from any dashboard really navigates back to landing */}
          <Route path="/" element={<Home />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
          </Suspense>
          </ErrorBoundary>
        </Layout>
      )}

      {/* Admin area routes without public Header/Footer */}
      {isAdminPath && (
        <AdminLayout>
          <ErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
          <Route path="/admin-dashboard" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminDashboard />} />} />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminUsers />} />} />
          <Route path="/admin/courses" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminCourses />} />} />
          <Route path="/admin/course-learning" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminCourseLearning />} />} />
          <Route path="/admin/lectures" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminLectures />} />} />
          <Route path="/admin/orders" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminOrders />} />} />
          <Route path="/admin/reviews" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminReviews />} />} />
          <Route path="/admin/faqs" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminFaq />} />} />
          <Route path="/admin/services" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminServices />} />} />
          <Route path="/admin/projects" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminProjects />} />} />
          <Route path="/admin/instructors" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminInstructors />} />} />
          <Route path="/admin/pyq" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminPYQ />} />} />
          <Route path="/admin/blog" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminBlog />} />} />
          <Route path="/admin/contact" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminContact />} />} />
          <Route path="/admin/newsletter" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminNewsletter />} />} />
          <Route path="/admin/approvals" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminApprovals />} />} />
          <Route path="/admin/interviews" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminInterviews />} />} />
          <Route path="/admin/task-reviews" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminTaskReviews />} />} />
          <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminAnalytics />} />} />
          <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminReports />} />} />
          <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminSettings />} />} />
          <Route path="/admin/internship" element={<ProtectedRoute allowedRoles={['admin']} element={<InternshipAdmin />} />} />
          <Route path="/admin/internship-openings" element={<ProtectedRoute allowedRoles={['admin']} element={<AdminInternshipOpenings />} />} />

          {/* "/admin" is the natural thing to type, so send it to the dashboard.
              Anything else under /admin that matches nothing gets the 404 page:
              this block is a separate <Routes> from the public one, so without a
              catch-all here an unmatched admin URL rendered an empty shell with
              no explanation and no way back. */}
          <Route path="/admin" element={<Navigate to="/admin-dashboard" replace />} />
          <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ErrorBoundary>
        </AdminLayout>
      )}
    </div>
  );
}

const styles = {
  routeFallback: {
    minHeight: '55vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '14px',
  },
  routeSpinner: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    border: '3px solid rgba(79, 70, 229, 0.18)',
    borderTopColor: '#4F46E5',
    animation: 'spin 0.8s linear infinite',
  },
  routeFallbackText: { fontSize: '14px', color: '#64748B' },
  loadingContainer: { minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#f8fafc', padding: '16px' },
  loadingCard: { backgroundColor: '#ffffff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', textAlign: 'center', maxWidth: '400px', width: '100%' },
  spinner: { border: '4px solid rgba(0, 0, 0, 0.1)', borderLeftColor: '#3b82f6', borderRadius: '50%', width: '40px', height: '40px', animation: 'spin 1s linear infinite', margin: '20px auto' },
  notFound: { padding: '50px', textAlign: 'center', fontSize: '24px', fontWeight: 'bold', color: '#ef4444' },
};

const spinnerStyles = `@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = spinnerStyles;
  document.head.appendChild(styleSheet);
}

export default function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <CartProvider>
              <CourseProvider>
                <NotificationProvider>
                  <RootContent />
                </NotificationProvider>
              </CourseProvider>
            </CartProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </Router>
  );
}